import { AlertTriangle } from "lucide-react";
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { errorMessage } from "../util";
import { Button } from "./Button";
import { Callout } from "./Display";
import { Field } from "./Field";
import { Input } from "./Input";
import { Modal } from "./Overlay";

export interface ConfirmOptions {
  title: ReactNode;
  description?: ReactNode;
  /** Defaults: "Confirm" / "Cancel". Name the action: "Delete account". */
  confirmLabel?: string;
  cancelLabel?: string;
  /** `danger` = red confirm button + warning icon. */
  tone?: "danger" | "primary";
  /** Type-to-confirm (e.g. the account's email) for irreversible actions. */
  requireText?: string;
  /**
   * Run the action from inside the dialog: it shows a spinner while pending,
   * stays open with the error if it throws, and resolves `true` on success.
   */
  action?: () => Promise<unknown>;
  /** Extra content under the description. */
  children?: ReactNode;
}

export interface ConfirmDialogProps extends Omit<ConfirmOptions, "action"> {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
  error?: ReactNode;
}

/** Controlled confirm dialog. Most code should use `useConfirm()` instead. */
export function ConfirmDialog({
  open,
  onConfirm,
  onCancel,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "primary",
  requireText,
  loading,
  error,
  children,
}: ConfirmDialogProps) {
  const [typed, setTyped] = useState("");
  const blocked = !!requireText && typed.trim().toLowerCase() !== requireText.trim().toLowerCase();
  return (
    <Modal
      open={open}
      onClose={() => {
        if (!loading) onCancel();
      }}
      dismissible={!loading}
      size="sm"
      title={title}
      description={description}
      icon={tone === "danger" ? <AlertTriangle size={20} /> : undefined}
      className={tone === "danger" ? "gf-confirm gf-confirm--danger" : "gf-confirm"}
      onSubmit={() => {
        if (!blocked && !loading) onConfirm();
      }}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={loading} data-autofocus={tone === "danger" && !requireText ? true : undefined}>
            {cancelLabel}
          </Button>
          <Button
            type="submit"
            variant={tone === "danger" ? "danger" : "primary"}
            loading={loading}
            disabled={blocked}
            data-autofocus={tone !== "danger" && !requireText ? true : undefined}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
      {requireText ? (
        <Field label={<>Type <b className="gf-confirm__phrase">{requireText}</b> to confirm</>}>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false} data-autofocus />
        </Field>
      ) : null}
      {error ? <Callout tone="danger">{error}</Callout> : null}
    </Modal>
  );
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;
const ConfirmContext = createContext<ConfirmFn | null>(null);

interface Pending {
  opts: ConfirmOptions;
  resolve: (ok: boolean) => void;
  key: number;
}

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const key = useRef(0);

  const confirm = useCallback<ConfirmFn>((opts) => {
    return new Promise<boolean>((resolve) => {
      setError(null);
      setLoading(false);
      setPending((prev) => {
        prev?.resolve(false);
        return { opts, resolve, key: ++key.current };
      });
    });
  }, []);

  async function onConfirm() {
    if (!pending) return;
    if (pending.opts.action) {
      setLoading(true);
      setError(null);
      try {
        await pending.opts.action();
      } catch (err) {
        setLoading(false);
        setError(errorMessage(err));
        return;
      }
      setLoading(false);
    }
    pending.resolve(true);
    setPending(null);
  }

  function onCancel() {
    pending?.resolve(false);
    setPending(null);
    setError(null);
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {pending ? (
        <ConfirmDialog
          key={pending.key}
          open
          {...pending.opts}
          loading={loading}
          error={error}
          onConfirm={() => void onConfirm()}
          onCancel={onCancel}
        />
      ) : null}
    </ConfirmContext.Provider>
  );
}

/**
 * Promise-based confirm:
 *   const confirm = useConfirm();
 *   if (await confirm({ title: "Delete this?", tone: "danger", confirmLabel: "Delete" })) …
 */
export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm() must be used inside <ConfirmProvider> (or <GfProvider>).");
  return ctx;
}
